import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Store } from '@ngxs/store';
import { OperationObserverService } from '@x/common/operation';
import {
  ChannelFetchContent,
  ChannelState,
  IChannelSelectOption,
} from '@x/ecommerce-shop/app/channel/state';
import { ShopStorage } from '@x/ecommerce-shop/app/core/shop-storage/shop-storage.service';
import { IShopConfig, SHOP_CONFIG } from '@x/ecommerce-shop/config/shop.config';
import { ShopChannelService } from '@x/ecommerce/shop-client';
import { Observable, of, switchMap } from 'rxjs';
import { catchError, filter, map, shareReplay } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ChannelContextService {
  constructor(
    private shopChannelService: ShopChannelService,
    @Inject(LOCALE_ID) private locale: string,
    private shopStorage: ShopStorage,
    private store: Store,
    @Inject(SHOP_CONFIG) private shopConfig: IShopConfig,
    private operation: OperationObserverService,
  ) {}

  suggestedChannel$(ipAddress?: string): Observable<string | null> {
    if (this.channelCode) return of(this.channelCode);

    return this.shopChannelService
      .fetchChannelSuggestions({
        locale: this.locale,
        ipAddress,
      })
      .pipe(
        map((channel) => {
          if (!channel) return null;

          const { usableChannel, preferredChannel, defaultChannel } = channel;

          const suggestedChannel = preferredChannel ?? usableChannel ?? defaultChannel;

          return suggestedChannel?.code ?? null;
        }),
        catchError((e) => {
          return of(null);
        }),
      );
  }

  get channelCode(): string | null {
    const channel =
      this.shopStorage.channel ?? this.store.selectSnapshot(ChannelState.activeChannelCode);

    if (channel) {
      this.shopStorage.channel = channel;
      return channel;
    }

    return null;
  }

  channelPickerOptions$: Observable<IChannelSelectOption[]> = this.store
    .select(ChannelState.channels)
    .pipe(
      map((channels) => {
        const externalShopLink = this.shopConfig.externalShopLink;
        return externalShopLink ? [...channels, externalShopLink] : [...channels];
      }),
    );

  channelContent$ = this.store.select(ChannelState.activeChannel).pipe(
    map((channel) => channel?.contentRef),
    filter(Boolean),
    switchMap((contentRef) => {
      return this.operation.observe(
        this.store
          .dispatch(new ChannelFetchContent(contentRef))
          .pipe(switchMap(() => this.store.selectOnce(ChannelState.activeChannelContent))),
      );
    }),
    shareReplay(),
  );
}
